import React from "react";
import { Card, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { ReportComboContext } from "./User/services/context";
import { mapOldFilterToNew } from "./User/services/services";
import { StylesReportComboUtils } from "./User/services/stylesReportComboUtils";
import {
	ColumnConfigUrlOptionReportComboApi,
	CompareFormatReportComboApi,
	ConfigReportComboApi,
	DataConfigReportComboApi,
	FlatReportComboApi,
	ResultDataFlatReportComboApi,
	ResultFlatReportComboApi,
	RowConfigReportComboApi,
} from "./User/services/types";
import { ReportComboUtils } from "./User/services/utils";

interface Props {
	config_flat: FlatReportComboApi;
	config: ConfigReportComboApi;
}

export const ReportComboItem = (props: Props) => {
	const reportComboService = React.useContext(ReportComboContext);

	const getUrl = (
		reportCfg: DataConfigReportComboApi,
		columnId?: string,
		aggregates?: string[],
		urlOptions?: ColumnConfigUrlOptionReportComboApi
	) => {
		if (!reportCfg) return undefined;
		const path = reportComboService.getPath(reportCfg.resource, reportCfg.filter);
		if (!path) return undefined;

		let filterString = reportCfg.filter;
		let groups = [] as string[];
		const configReportColumn = reportCfg.columns.filter((x) => x.id === columnId)[0];
		if (configReportColumn?.group && configReportColumn.group !== "NONE") {
			groups.push(encodeURIComponent(configReportColumn.group));
		}
		groups.push(...reportCfg.groups.filter((x) => x !== "NONE"));
		if (urlOptions && urlOptions.groups) {
			groups.push(...urlOptions.groups);
		}
		if (groups.length === 0) groups = ["NONE"];

		if (configReportColumn.filter) {
			filterString += `ç${configReportColumn.filter}`;
		}

		const filter = mapOldFilterToNew(filterString);
		let queryParams = `?f=${btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}${filter}`)))}`;

		queryParams += `&groups=${encodeURIComponent(`${groups.join("¥")}`)}`;

		if (aggregates) {
			queryParams += `&c=${aggregates.join(",").replaceAll(" + ", ",")}`;
		}

		return `${path}${queryParams}`;
	};

	const renderCompareValue = (value: string, compareValue: string, format: CompareFormatReportComboApi) => {
		if (format === "VALUE") return ReportComboUtils.renderValueObj(compareValue);

		if (format === "PERCENT" || format === "GROWTH") {
			// Convert the string values to numbers
			const numericValue = ReportComboUtils.getValueFromValueObj(value);
			const numericCompareValue = ReportComboUtils.getValueFromValueObj(compareValue);

			// Check if the numericCompareValue is zero to avoid division by zero
			if (numericCompareValue === 0) {
				return "~ %"; // or "N/A %" depending on what you prefer to show in this case
			}

			// Perform the division and multiply by 100 to get a percentage
			let percentage = (numericValue / numericCompareValue) * 100;
			if (format === "GROWTH") percentage = ((numericValue - numericCompareValue) / numericCompareValue) * 100;

			// Return the result formatted as a percentage string
			return `${percentage.toFixed(0)} %`;
		}

		return compareValue;
	};

	const renderValue = (dataId: string, columnId: string, kpiData: ResultDataFlatReportComboApi) => {
		let url: string | undefined = undefined;
		const columnCfg = props.config_flat.config_table.columns.filter((column) => column.id === columnId)[0];
		if (dataId) {
			const reportCfg = props.config.data.filter((x) => x.id === dataId)[0];
			const keys = Object.keys(kpiData.aggregate);
			url = getUrl(reportCfg, ReportComboUtils.getDataColumnId(columnCfg), keys, columnCfg.url_options);
		}
		return (
			<>
				{url ? (
					<Link to={url}>
						{ReportComboUtils.renderValueObj(kpiData.value, columnCfg.format, columnCfg.custom_format)}
					</Link>
				) : (
					<>
						<>{ReportComboUtils.renderValueObj(kpiData.value, columnCfg.format, columnCfg.custom_format)}</>
					</>
				)}
			</>
		);
	};

	const renderSubReports = (configRow: RowConfigReportComboApi, result: ResultFlatReportComboApi, id: string) => {
		if (!result.sub_report || result.sub_report.length <= 0) return null;

		return (
			<React.Fragment key={`${result.data_id}`}>
				{renderSingleRow(configRow, result, id)}
				{result.sub_report.map((subReport) => {
					const idSubReport = `${`${id}-${subReport.name}`}`;
					return (
						<React.Fragment key={idSubReport}>
							{renderSingleRow(configRow, subReport, idSubReport, 1)}
						</React.Fragment>
					);
				})}
			</React.Fragment>
		);
	};

	const renderSingleRow = (
		configRow: RowConfigReportComboApi,
		result: ResultFlatReportComboApi,
		id: string,
		level?: number
	) => {
		if (configRow.type === "DIVIDER") {
			return (
				<tr>
					<td>{configRow.name}</td>
				</tr>
			);
		}
		return (
			<>
				<tr>
					<td style={{ paddingLeft: level ? 25 * level : "inherit" }}>{result.name}</td>
					{result.data.map((kpiData) => {
						const columnCfg = props.config_flat.config_table.columns.filter(
							(column) => column.id === kpiData.column_id
						)[0];
						return (
							<td
								key={`${id}-${kpiData.id}`}
								style={{
									...StylesReportComboUtils.getColumneStyles(columnCfg, kpiData.value),
									textAlign: "right",
								}}
							>
								{renderValue(result.data_id, kpiData.column_id, kpiData)}
							</td>
						);
					})}
				</tr>
				{renderCompare(configRow, result.data, id, level)}
			</>
		);
	};

	const renderCompare = (
		configRow: RowConfigReportComboApi,
		subReport: ResultDataFlatReportComboApi[],
		id: string,
		level?: number
	) => {
		if (!configRow.compare) return null;

		return (
			<>
				{configRow.compare.map((compareItem) => {
					return (
						<tr key={`data-compare-${compareItem.name}`}>
							<td style={{ paddingLeft: level ? 25 * (level + 1) : 25 }}>{compareItem.name}</td>
							{subReport.map((kpiData) => {
								if (!kpiData.compare) return null;
								const compareValue = kpiData.compare.filter((x) => x.compare_id === compareItem.id)[0];
								return (
									<td key={`${id}-${kpiData.id}-compare`}>
										{renderCompareValue(kpiData.value, compareValue.value, compareItem.format)}
									</td>
								);
							})}
						</tr>
					);
				})}
			</>
		);
	};

	return (
		<Card className={"churn-card mb-4"}>
			<Card.Body>
				<div className="mb-1 d-flex align-items-center">
					<h5 className="mb-0">{props.config_flat.name}</h5>
				</div>
				<span className="text-muted">{props.config_flat.description}</span>
				<div>
					<Table>
						<thead>
							<tr>
								<th>Nazwa</th>
								{props.config_flat.config_table.columns?.map((column) => {
									if (!column.values) {
										return <th key={`${column.id}-Summary`}>{column.name}</th>;
									}
									return (
										<React.Fragment key={`${column.id}-values`}>
											{column.values.map((columnValue) => {
												return <th key={`${column.id}-${columnValue.id}`}>{columnValue.id}</th>;
											})}
										</React.Fragment>
									);
								})}
							</tr>
						</thead>
						<tbody>
							{props.config_flat?.results?.map((result) => {
								const configRow = props.config_flat.config_table.rows.filter(
									(x) => x.id === result.row_id
								)[0];
								const id = `${`data-${result.row_id}`}`;
								return (
									<React.Fragment key={`${id}`}>
										{result.sub_report && result.sub_report.length > 0
											? renderSubReports(configRow, result, id)
											: renderSingleRow(configRow, result, id)}
									</React.Fragment>
								);
							})}
						</tbody>
					</Table>
				</div>
			</Card.Body>
		</Card>
	);
};
